import * as SliderPrimitive from "@radix-ui/react-slider";
import { cva, type VariantProps } from "class-variance-authority";
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ForwardedRef,
  forwardRef,
} from "react";

const sliderVariants = cva(
  "relative flex touch-none select-none items-center",
  {
    variants: {
      variant: {
        orange: "[--slider-color:theme(colors.orange.500)]",
        purple: "[--slider-color:theme(colors.purple.500)]",
      },
      size: {
        sm: "[--thumb-size:theme(spacing.4)]",
        lg: "[--thumb-size:theme(spacing.6)]",
      },
    },
    defaultVariants: {
      variant: "purple",
      size: "sm",
    },
  },
);

type SliderProps = Omit<
  ComponentPropsWithoutRef<typeof SliderPrimitive.Root>,
  "orientation"
> &
  VariantProps<typeof sliderVariants>;
function Slider(
  { className, variant, size, ...props }: SliderProps,
  ref: ForwardedRef<ElementRef<typeof SliderPrimitive.Root>>,
) {
  return (
    <SliderPrimitive.Root
      ref={ref}
      className={sliderVariants({ variant, size, className })}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className="relative h-1 w-full grow rounded-full bg-black-200 before:absolute before:h-6 before:top-1/2 before:-translate-y-1/2 before:w-full"
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className="absolute rounded-full h-full bg-[var(--slider-color)]"
        />
      </SliderPrimitive.Track>

      <SliderPrimitive.Thumb
        data-slot="slider-thumb"
        className="shadow-md relative before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:size-6 block size-[var(--thumb-size)] cursor-pointer rounded-full border-2 focus-visible:outline-none border-white bg-[var(--slider-color)] disabled:pointer-events-none"
      />
    </SliderPrimitive.Root>
  );
}

const SliderWithRef = forwardRef(Slider);
SliderWithRef.displayName = SliderPrimitive.Root.displayName;

export default SliderWithRef;
